import { Action, createSelector, Selector, State, StateContext, Store } from '@ngxs/store';
import {
  ApprovalTypes,
  GeneralLedgerDownloadTypes,
  GeneralLedgerExportTypes,
  GlCheckData,
  GLConfiguration,
  GlQboAccount,
  GlTotalsData
} from '../../models/general-ledger';
import { GeneralLedgerService } from '../../services/general-ledger.service';
import { Injectable } from '@angular/core';
import { ClientSelectionState } from '../ClientSelectionState/ClientSelection.state';
import { mergeMap, tap } from 'rxjs/operators';
import { append, patch } from '@ngxs/store/operators';
import { of } from 'rxjs';
import { downloadFile } from '../../tools/reports-tools';
import { ModalService } from '../../services/modal.service';

export class GlResetState {
  static readonly type = '[GeneralLedger] Reset State';
}
export class GlClearSelected {
  static readonly type = '[GeneralLedger] Clear Selected';
}
export class GlInsertConfig {
  static readonly type = '[GeneralLedger] Config inserted';
  constructor(public config: GLConfiguration) {}
}
export class GlSelectedConfigChanged {
  static readonly type = '[GeneralLedger] Config Selection Changed';
  constructor(public configId: number) {}
}
export class GlTabChanged {
  static readonly type = '[GeneralLedger] Tab Selection Changed';
  constructor(public newTab: number) {}
}

export class GlLoadConfigs {
  static readonly type = '[GeneralLedger] LoadConfigs';
}

export class GlLoadQboAccounts {
  static readonly type = '[GeneralLedger] LoadQboAccounts';
  constructor(public configId: number) {}
}

export class GlDoExport {
  static readonly type = '[GeneralLedger] DoExport';

  constructor(public configId: number) {}
}

export interface IGeneralLedgerState {
  currentlySelectedTab: number;
  glConfigs: GLConfiguration[];
  currentlySelectedConfig: number | null;
  balanceData: GlCheckData[] | GlTotalsData[];
  accounts: GlQboAccount[];
  classes: GlQboAccount[];
}

@State<IGeneralLedgerState>({
  name: 'generalLedger',
  defaults: {
    currentlySelectedTab: 1,
    glConfigs: [],
    currentlySelectedConfig: null,
    balanceData: [],
    accounts: [],
    classes: []
  }
})
@Injectable()
export class GeneralLedgerState {
  constructor(
    private store: Store,
    private glService: GeneralLedgerService,
    private modalService: ModalService
  ) {}

  @Selector()
  static getCurrentTab(state: IGeneralLedgerState) {
    return state.currentlySelectedTab;
  }
  @Selector()
  static getConfigs(state: IGeneralLedgerState) {
    return [...state.glConfigs].sort((a, b) => {
      if (a.Name < b.Name) return -1;
      if (a.Name > b.Name) return 1;
      return 0;
    });
  }
  @Selector()
  static getSelectedConfig(state: IGeneralLedgerState) {
    return state.currentlySelectedConfig;
  }

  @Selector()
  static getSelectedConfigObject(state: IGeneralLedgerState) {
    return state.glConfigs.find(x => x.Id == state.currentlySelectedConfig);
  }

  @Selector()
  static getQboAccounts(state: IGeneralLedgerState) {
    return state.accounts;
  }

  @Selector()
  static getQboClasses(state: IGeneralLedgerState) {
    return state.classes;
  }

  @Selector()
  static hasOneAutomatic(state: IGeneralLedgerState) {
    const current = this.getSelectedConfigObject(state);
    if (current != null && current.SubmissionType === ApprovalTypes.Automatic) return false;
    return state.glConfigs.some(x => x.SubmissionType === ApprovalTypes.Automatic);
  }

  @Selector()
  static getBalance(state: IGeneralLedgerState) {
    const config = this.getSelectedConfigObject(state);
    if (state.currentlySelectedConfig == null || state.currentlySelectedConfig == 0) {
      return null;
    }
    if (config.Type == GeneralLedgerDownloadTypes.Totals) {
      const totals = state.balanceData as GlTotalsData[];
      let total = 0;
      totals.forEach(item => {
        if (item.Type == 'debit') {
          //debiting an account reduces the balance
          total -= item.Amount;
        } else if (item.Type == 'credit') {
          //crediting an account increases the balance
          total += item.Amount;
        }
      });
      return total;
    } else {
      const checks = state.balanceData as GlCheckData[];
      let total = 0;
      checks.forEach(check => {
        total += check.NetPay;
      });
      return total;
    }
  }

  static getFormattedName(name: string) {
    return createSelector([GeneralLedgerState], (state: IGeneralLedgerState) => {
      if (state.glConfigs.some(x => x.Name.includes(name))) {
        const count = state.glConfigs.filter(x => x.Name.includes(name)).length;
        return `${name}_${count + 1}`;
      }
      return name;
    });
  }

  static doesNameExist(name: string) {
    return createSelector([GeneralLedgerState], (state: IGeneralLedgerState) => {
      const currentState = this.getSelectedConfigObject(state);
      return {
        Result: state.glConfigs.some(x => x.Name.toLowerCase() === name.toLowerCase()),
        IsCurrentConfig: currentState.Name.toLowerCase() === name.toLowerCase()
      };
    });
  }

  @Action(GlTabChanged)
  tabChanged(ctx: StateContext<IGeneralLedgerState>, { newTab }: GlTabChanged) {
    ctx.patchState({
      currentlySelectedTab: newTab
    });
  }

  @Action(GlLoadConfigs)
  loadConfigs(ctx: StateContext<IGeneralLedgerState>) {
    return this.store.selectOnce(ClientSelectionState.clientCode).pipe(
      mergeMap(code => {
        return this.glService.getGLConfigurations(code);
      }),
      tap(result => {
        ctx.patchState({
          glConfigs: result
        });
      })
    );
  }

  @Action(GlLoadQboAccounts)
  loadQboAccounts(ctx: StateContext<IGeneralLedgerState>, { configId }: GlLoadQboAccounts) {
    const state = ctx.getState();
    const config = GeneralLedgerState.getSelectedConfigObject(state);
    if (config.ExportFileType == GeneralLedgerExportTypes.QBO) {
      //grab the QBO data
      return this.store.selectOnce(ClientSelectionState.clientCode).pipe(
        mergeMap(clientCode => {
          return this.glService.getQboAccounts(clientCode, configId).pipe(
            mergeMap(result => {
              ctx.patchState({
                accounts: result
              });
              return this.glService.getQboClasses(clientCode, configId);
            }),
            tap(classes => {
              ctx.patchState({
                classes
              });
            })
          );
        })
      );
    }
    return of([]);
  }

  @Action(GlSelectedConfigChanged)
  selectConfig(ctx: StateContext<IGeneralLedgerState>, { configId }: GlSelectedConfigChanged) {
    if (configId != null && configId > 0) {
      const state = ctx.getState();
      const config = state.glConfigs.find(x => x.Id == configId);
      let clientCode: string = '';
      return this.store.selectOnce(ClientSelectionState.clientCode).pipe(
        mergeMap(code => {
          clientCode = code;
          if (config.Type == GeneralLedgerDownloadTypes.Totals) {
            return this.glService.getTotalsData(code, configId);
          } else {
            return this.glService.getChecksData(code, configId);
          }
        }),
        tap(result => {
          ctx.patchState({
            currentlySelectedConfig: configId,
            balanceData: result
          });
        })
      );
    } else {
      ctx.patchState({
        currentlySelectedConfig: configId
      });
      return of();
    }
  }
  @Action(GlClearSelected)
  clearSelected(ctx: StateContext<IGeneralLedgerState>) {
    ctx.patchState({
      currentlySelectedConfig: null,
      balanceData: [],
      accounts: []
    });
  }

  @Action(GlResetState)
  reset(ctx: StateContext<IGeneralLedgerState>) {
    ctx.setState({
      currentlySelectedTab: 1,
      glConfigs: [],
      currentlySelectedConfig: null,
      balanceData: [],
      accounts: [],
      classes: []
    });
  }

  @Action(GlInsertConfig)
  insertConfig(ctx: StateContext<IGeneralLedgerState>, { config }: GlInsertConfig) {
    ctx.setState(
      patch<IGeneralLedgerState>({
        glConfigs: append<GLConfiguration>([config])
      })
    );
  }

  @Action(GlDoExport)
  exportFile(ctx: StateContext<IGeneralLedgerState>, { configId }: GlDoExport) {
    const state = ctx.getState();
    const config = state.glConfigs.find(x => x.Id == configId);
    return this.store.selectOnce(ClientSelectionState.clientCode).pipe(
      mergeMap(code => {
        switch (config.ExportFileType) {
          case GeneralLedgerExportTypes.CSV:
            return this.glService
              .exportToCsv(code, configId)
              .pipe(tap(response => downloadFile(response, 'export.csv')));
          case GeneralLedgerExportTypes.IIF:
            return this.glService
              .exportToQbDesktop(code, configId)
              .pipe(tap(response => downloadFile(response, 'export.iif')));
          case GeneralLedgerExportTypes.QBO:
            return this.glService.exportToQbo(code, configId);
        }
      })
    );
  }
}
